import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { authApiService } from '../services/authApiService';
import { config } from '../config/environment';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { SignupProgress } from '../components/SignupProgress';
import { useSignupSession } from '../contexts/SignupSessionContext';
import { debugError } from '../utils/debug';

/**
 * Self-serve registration: collect profile + password, send verification email.
 */
export const RegisterStartPage: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;
    const socialSignInEnabled = flags['SocialSignIn'] ?? false;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { session, updateSession } = useSignupSession();
    const [email, setEmail] = useState(session.email || '');
    const [firstName, setFirstName] = useState(session.firstName || '');
    const [lastName, setLastName] = useState(session.lastName || '');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [oauthBusy, setOauthBusy] = useState<'google' | 'apple' | 'microsoft' | null>(null);

    useEffect(() => {
        document.title = 'Create account | Calculist';
    }, []);

    useEffect(() => {
        const oauthErr = searchParams.get('oauthError');
        if (oauthErr) {
            setError(decodeURIComponent(oauthErr));
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    if (flagsLoading) {
        return null;
    }

    if (!selfServeSignup) {
        return <Navigate to="/auth/login" replace />;
    }

    const handleGoogle = async () => {
        setError(null);
        setOauthBusy('google');
        try {
            const { data } = await authApiService.signupOAuthGoogleStart('/auth/register/complete');
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setError('Google sign-in is not available.');
        } catch (err: unknown) {
            debugError('signupOAuthGoogleStart', err);
            setError(err instanceof Error ? err.message : 'Could not start Google sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    const handleApple = async () => {
        setError(null);
        setOauthBusy('apple');
        try {
            const { data } = await authApiService.signupOAuthAppleStart('/auth/register/verify');
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setError('Apple sign-in is not available.');
        } catch (err: unknown) {
            debugError('signupOAuthAppleStart', err);
            setError(err instanceof Error ? err.message : 'Could not start Apple sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    const handleMicrosoft = async () => {
        setError(null);
        setOauthBusy('microsoft');
        try {
            const { data } = await authApiService.signupOAuthMicrosoftStart('/auth/register/complete');
            if (data.authorizationUrl) {
                window.location.assign(data.authorizationUrl);
                return;
            }
            setError('Microsoft sign-in is not available.');
        } catch (err: unknown) {
            debugError('signupOAuthMicrosoftStart', err);
            setError(err instanceof Error ? err.message : 'Could not start Microsoft sign-in.');
        } finally {
            setOauthBusy(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (/[#/<>]/.test(email) || /https?:/i.test(email)) {
            setError('Email address has invalid characters.');
            return;
        }
        if (config.turnstileSiteKey && !turnstileToken) {
            setError('Please complete the verification challenge.');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await authApiService.signupRegisterStart({
                email: email.trim(),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                turnstileToken,
            });
            const token = (data as { sessionToken?: string }).sessionToken ?? '';
            updateSession({ sessionToken: token, email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
            navigate('/auth/register/plan');
        } catch (err: unknown) {
            debugError('signupRegisterStart', err);
            const msg = err instanceof Error ? err.message : 'Registration could not be started.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            <div style={themedStyles.card.default}>
                <SignupProgress currentStep={1} />

                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img
                        src="/logo-name.svg"
                        alt="Calculist"
                        style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }}
                    />
                    <p style={themedStyles.text.muted}>Create your account</p>
                </div>

                {error && (
                    <div style={themedStyles.alert.error}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <div style={{ flex: 1 }}>{error}</div>
                    </div>
                )}

                {!config.turnstileSiteKey && (
                    <p style={{ ...themedStyles.text.muted, fontSize: theme.typography.fontSize.sm }}>
                        Turnstile is not configured in this environment; registration may only work when the API allows bypass verification.
                    </p>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <label htmlFor="reg-email" style={themedStyles.text.label}>Email</label>
                        <input
                            id="reg-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            disabled={submitting}
                            style={themedStyles.input.default}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <label htmlFor="reg-fn" style={themedStyles.text.label}>First name</label>
                        <input
                            id="reg-fn"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            autoComplete="given-name"
                            disabled={submitting}
                            style={themedStyles.input.default}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <label htmlFor="reg-ln" style={themedStyles.text.label}>Last name</label>
                        <input
                            id="reg-ln"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                            autoComplete="family-name"
                            disabled={submitting}
                            style={themedStyles.input.default}
                        />
                    </div>
                    {!!config.turnstileSiteKey && (
                        <TurnstileWidget siteKey={config.turnstileSiteKey} onTokenChange={setTurnstileToken} />
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        style={{
                            ...themedStyles.button.primary,
                            ...(submitting ? themedStyles.button.disabled : {}),
                            padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                        }}
                    >
                        {submitting ? 'Sending…' : 'Continue'}
                    </button>
                </form>

                {socialSignInEnabled && (
                    <>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.md,
                            marginTop: theme.spacing.sm,
                        }}>
                            <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                            <span style={{ ...themedStyles.text.muted, fontSize: theme.typography.fontSize.sm, whiteSpace: 'nowrap' }}>Or continue with</span>
                            <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
                            <button
                                type="button"
                                onClick={() => void handleGoogle()}
                                disabled={!!oauthBusy || submitting}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: theme.spacing.sm, width: '100%',
                                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                    fontSize: theme.typography.fontSize.md,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    borderRadius: 8, border: '1px solid #dadce0',
                                    backgroundColor: '#ffffff', color: '#3c4043',
                                    cursor: oauthBusy || submitting ? 'not-allowed' : 'pointer',
                                    opacity: oauthBusy || submitting ? 0.7 : 1,
                                }}
                            >
                                <i className="fab fa-google" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                                {oauthBusy === 'google' ? 'Redirecting…' : 'Continue with Google'}
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleMicrosoft()}
                                disabled={!!oauthBusy || submitting}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: theme.spacing.sm, width: '100%',
                                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                    fontSize: theme.typography.fontSize.md,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    borderRadius: 8, border: 'none',
                                    backgroundColor: '#0078D4', color: '#ffffff',
                                    cursor: oauthBusy || submitting ? 'not-allowed' : 'pointer',
                                    opacity: oauthBusy || submitting ? 0.7 : 1,
                                }}
                            >
                                <i className="fab fa-microsoft" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                                {oauthBusy === 'microsoft' ? 'Redirecting…' : 'Continue with Microsoft'}
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleApple()}
                                disabled={!!oauthBusy || submitting}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: theme.spacing.sm, width: '100%',
                                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                                    fontSize: theme.typography.fontSize.md,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    borderRadius: 8, border: 'none',
                                    backgroundColor: '#000000', color: '#ffffff',
                                    cursor: oauthBusy || submitting ? 'not-allowed' : 'pointer',
                                    opacity: oauthBusy || submitting ? 0.7 : 1,
                                }}
                            >
                                <i className="fab fa-apple" style={{ fontSize: theme.typography.fontSize.lg }} aria-hidden />
                                {oauthBusy === 'apple' ? '…' : 'Continue with Apple'}
                            </button>
                        </div>
                    </>
                )}

                <p style={{ marginTop: theme.spacing.lg, textAlign: 'center', ...themedStyles.text.muted }}>
                    Already have an account?{' '}
                    <Link to="/auth/login" style={{ color: theme.colors.primary }}>Sign in</Link>
                </p>
            </div>
        </div>
    );
};
